import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core';
import { ErrorCodeQuery } from '../../../../../shared/models/error-code/error-code-query';
import { FormsModule } from '@angular/forms';
import { CloseIconComponent } from '../../../icons/close-icon/close-icon.component';
import { errorToString, pascalCaseToWords } from '../../../../common/common-utils';
import { ErrorCodeCategory } from '../../../../../shared/enums/error-code-category';
import { ProjectApiService } from '../../../../services/api/project-api.service';
import { SelectionOptionValue } from '../../../../../shared/models/internal/selection-option-value';
import { Project } from '../../../../../shared/models/project';
import { NwNumber } from '../../../../../shared/models/nw-number/nw-number';
import { NwNumberApiService } from '../../../../services/api/nw-number-api.service';
import { BaseFilter } from '../../../base/base-filter';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { ThreeStateToggleComponent } from '../../../common/three-state-toggle/three-state-toggle.component';

@Component({
  selector: 'app-error-code-filter',
  imports: [FormsModule, CloseIconComponent, AgentActionDirective, ThreeStateToggleComponent],
  templateUrl: './error-code-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './error-code-filter.component.scss',
})
export class ErrorCodeFilterComponent extends BaseFilter<ErrorCodeQuery> {
  @Input() public uniqueId = '';
  public pascalCaseToWords = pascalCaseToWords;
  public categories: Map<ErrorCodeCategory, boolean> = new Map<ErrorCodeCategory, boolean>();
  public selectedProjectName = 'None';
  public projects: SelectionOptionValue[] = [];
  public nwNumbers: Map<NwNumber, boolean> = new Map<NwNumber, boolean>();

  private readonly projectApi = inject(ProjectApiService);
  private readonly nwNumberApi = inject(NwNumberApiService);

  constructor() {
    const defaultFilter: ErrorCodeQuery = {
      searchInCode: true,
      searchInMessage: true,
    };
    super(400, defaultFilter);
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.initErrorCodeCategories();
    this.initProjects();
    this.initNwNumbers();
    this.searchQuery.searchInCode = this.defaultFilter.searchInCode;
    this.searchQuery.searchInMessage = this.defaultFilter.searchInMessage;
  }

  public override resetFilters(): void {
    super.resetFilters();
    this.initProjects();
    this.initNwNumbers();
    this.initTranslationStates();
    this.initErrorCodeCategories();
  }

  public toggleErrorCodeCategory(category: ErrorCodeCategory): void {
    const currentCategory = this.categories.get(category) ?? false;
    this.categories.set(category, !currentCategory);
    this.searchQuery.categories = Array.from(this.categories.entries())
      .filter((entry) => entry[1])
      .map((entry) => entry[0]);
    this.emitFilterChanged();
  }

  public toggleNwNumber(nwNumber: NwNumber): void {
    const currentNwNumber = this.nwNumbers.get(nwNumber) ?? false;
    this.nwNumbers.set(nwNumber, !currentNwNumber);
    this.searchQuery.nwNumberIds = Array.from(this.nwNumbers.entries())
      .filter((entry) => entry[1])
      .map((entry) => entry[0].id ?? '');
    this.emitFilterChanged();
  }

  public selectProject(id?: string, name?: string): void {
    this.selectedProjectName = name ?? '';

    this.searchQuery.projectId = !id || id === '' ? undefined : id;
    this.emitFilterChanged();
  }

  private initErrorCodeCategories(): void {
    for (const category of this.getAllErrorCodeCategories()) {
      this.categories.set(category, false);
    }
  }

  private getAllErrorCodeCategories(): ErrorCodeCategory[] {
    return Object.values(ErrorCodeCategory);
  }

  private initNwNumbers(): void {
    this.nwNumbers.clear();
    this.nwNumberApi.getAllNwNumbers().subscribe({
      next: (nwNumbers) => {
        nwNumbers.reduce((m, item) => {
          m.set(item, false);
          return m;
        }, this.nwNumbers);
      },
    });
  }

  private initProjects(): void {
    const dummyEntry = {
      title: 'None',
      id: '',
      selected: true,
    };

    this.selectedProjectName = dummyEntry.title;

    this.projects = [dummyEntry];

    this.projectApi.getProjects().subscribe({
      next: (projects) => {
        this.projects = projects.map((project: Project) => ({
          title: project.name ?? '',
          id: project.id ?? '',
          selected: false,
        }));

        this.projects.unshift(dummyEntry);
      },
      error: (error) => {
        console.error('Error fetching projects:', errorToString(error));
      },
    });
  }
}
